import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { Box, Button, Card, Collapse, FloatingNotification, Text, TextButton } from 'wix-style-react';
import { DataHooks } from '@src/constants/data-hooks';
import { ChevronUp, StatusAlert, ChevronRight } from 'wix-ui-icons-common';
import { sendBISetupPrivacyClick, SetupPrivacyAction } from '@src/context/actions/bi';

import * as css from './setup-privacy-policy.scss';

export interface IProps extends InjectedTranslateProps {
  className?: string;
  learnKBLink: string;
  addKBLink: string;
}

export interface IState {
  isOpen: boolean;
}

export class InnerSetupPrivacyPolicyComponent extends React.PureComponent<IProps, IState> {
  state: IState = {
    isOpen: false,
  };
  toggleActionClicked = () => {
    this.setState(({ isOpen }) => {
      if (!isOpen) {
        sendBISetupPrivacyClick(SetupPrivacyAction.LearnHow);
      }

      return { isOpen: !isOpen };
    });
  };

  onLearnCreatePrivacyPolicyClick = () => {
    sendBISetupPrivacyClick(SetupPrivacyAction.SubLearnMore);
    window.open(this.props.learnKBLink, '_blank');
  };

  onAddPrivacyPolicyClick = () => {
    sendBISetupPrivacyClick(SetupPrivacyAction.SubLearnHow);
    window.open(this.props.addKBLink, '_blank');
  };

  render() {
    const { t, className } = this.props;
    const { isOpen } = this.state;

    const renderShowMoreButton = () => (
      <Button
        dataHook={DataHooks.SetupPrivacyMoreButton}
        className={css.textButton}
        priority="secondary"
        skin="standard"
        onClick={this.toggleActionClicked}
        suffixIcon={<ChevronRight />}
      >
        {this.props.t('learn.how')}
      </Button>
    );

    const renderShowLessButton = () => (
      <TextButton
        dataHook={DataHooks.SetupPrivacyLessButton}
        className={css.textButton}
        priority="secondary"
        skin="standard"
        onClick={this.toggleActionClicked}
        suffixIcon={<ChevronUp />}
      >
        {this.props.t('less')}
      </TextButton>
    );

    const renderExpandedContent = () => (
      <Card>
        <Box>
          <Text>{t('setup.privacy.policy.content.title')}</Text>
        </Box>
        <Box marginTop={'12px'}>
          <Text>
            {t('setup.privacy.policy.content.step1')}
            <TextButton
              className={css.textButton}
              dataHook={DataHooks.SetupPrivacyLearnMoreButton}
              onClick={this.onLearnCreatePrivacyPolicyClick}
            >
              {t('learn.more')}
            </TextButton>
          </Text>
        </Box>
        <Box marginTop={'6px'}>
          <Text>
            {t('setup.privacy.policy.content.step2')}
            <TextButton
              className={css.textButton}
              dataHook={DataHooks.SetupPrivacyLearnHowButton}
              onClick={this.onAddPrivacyPolicyClick}
            >
              {t('learn.how')}
            </TextButton>
          </Text>
        </Box>
        <Box marginTop={'18px'}>
          <FloatingNotification
            dataHook={DataHooks.SetupPrivacyFloatingNotification}
            prefixIcon={<StatusAlert size={'24px'} />}
            showCloseButton={false}
            width={'100%'}
            type={'warning'}
            text={t('setup.privacy.policy.content.disclaimer')}
          />
        </Box>
      </Card>
    );

    return (
      <Card className={className} dataHook={DataHooks.SetupPrivacyContainer}>
        <Card.Header
          dataHook={DataHooks.SetupPrivacyHeader}
          title={t('setup.privacy.policy.title')}
          subtitle={t('setup.privacy.policy.subtitle')}
          suffix={isOpen ? renderShowLessButton() : renderShowMoreButton()}
        />
        {isOpen ? <Card.Divider /> : null}
        <Collapse open={isOpen}>
          <Card.Content>{renderExpandedContent()}</Card.Content>
        </Collapse>
      </Card>
    );
  }
}
